import { Context, Effect, Layer, Ref, Scope } from "effect";
import { Atom, AtomRegistry, AsyncResult } from "effect/unstable/reactivity";
import { coreBindings, type Keybinding } from "./model.ts";

export class KeybindRegistry extends Context.Service<KeybindRegistry, {
  readonly snapshot: Atom.Atom<AsyncResult.AsyncResult<readonly Keybinding[]>>;
  readonly register: (commandId: string, defaultKeys: readonly string[], description: string) => Effect.Effect<void>;
  readonly override: (commandId: string, keys: readonly string[]) => Effect.Effect<void>;
  readonly getKeys: (commandId: string) => Effect.Effect<readonly string[]>;
  readonly getBinding: (commandId: string) => Effect.Effect<Keybinding | undefined>;
  readonly getAll: Effect.Effect<readonly Keybinding[]>;
  readonly reset: Effect.Effect<void>;
}>()("workerstation/frontend/KeybindRegistry") {}

const owned = (binding: Keybinding): Keybinding => Object.freeze({
  ...binding,
  defaultKeys: Object.freeze([...binding.defaultKeys]),
  ...(binding.userKeys === undefined ? {} : { userKeys: Object.freeze([...binding.userKeys]) })
});

export const KeybindRegistryLive = (owner: Scope.Scope) => Layer.effect(KeybindRegistry, Effect.gen(function* () {
  const closed = yield* Ref.make(false);
  const entries = yield* Ref.make<ReadonlyMap<string, Keybinding>>(new Map(coreBindings().map(binding => [binding.commandId, owned(binding)])));
  const registry = yield* AtomRegistry.AtomRegistry;
  const values = yield* Ref.make<readonly Keybinding[]>(Object.freeze([...(yield* Ref.get(entries)).values()]));
  const snapshot = Atom.make(Ref.get(values));
  const update = Effect.fn("KeybindRegistry.update")(function* (change: (state: ReadonlyMap<string, Keybinding>) => ReadonlyMap<string, Keybinding>) {
    if (owner.state._tag === "Closed" || (yield* Ref.get(closed))) return yield* Effect.interrupt;
    yield* Ref.update(entries, change);
    yield* Ref.set(values, Object.freeze([...(yield* Ref.get(entries)).values()]));
    yield* Effect.sync(() => registry.refresh(snapshot));
  });
  yield* Effect.addFinalizer(() => Ref.set(closed, true).pipe(
    Effect.andThen(Ref.set(entries, new Map())),
    Effect.andThen(Ref.set(values, []))
  ));
  return KeybindRegistry.of({
    snapshot,
    register: (commandId, defaultKeys, description) => update(state => {
      const existing = state.get(commandId);
      return new Map(state).set(commandId, owned({ ...existing, commandId, defaultKeys, description }));
    }),
    override: (commandId, keys) => update(state => {
      const existing = state.get(commandId);
      return new Map(state).set(commandId, owned(existing
        ? { ...existing, userKeys: keys }
        : { commandId, defaultKeys: keys, userKeys: keys, description: commandId }));
    }),
    getKeys: commandId => Ref.get(entries).pipe(Effect.map(state => {
      const binding = state.get(commandId);
      return binding?.userKeys ?? binding?.defaultKeys ?? [];
    })),
    getBinding: commandId => Ref.get(entries).pipe(Effect.map(state => state.get(commandId))),
    getAll: Ref.get(values),
    reset: update(() => new Map())
  });
}));
